import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Check, Building2, User } from 'lucide-react';
import CaptchaWidget from '../components/CaptchaWidget';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';

const RegisterPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    phone: '',
  });
  const [isCompany, setIsCompany] = useState(false);
  const [fiscalData, setFiscalData] = useState({
    fiscal_name: '',
    cif: '',
    vat_number: '',
    fiscal_address: '',
    fiscal_city: '',
    fiscal_postal_code: '',
    fiscal_province: '',
    fiscal_country: 'España',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFiscalChange = (e) => {
    setFiscalData({ ...fiscalData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('Las contraseñas no coinciden'));
      return;
    }

    if (formData.password.length < 8) {
      toast.error(t('La contraseña debe tener al menos 8 caracteres'));
      return;
    }

    setLoading(true);

    try {
      const fiscal = (fiscalData.cif || fiscalData.fiscal_name) ? fiscalData : null;
      const res = await register(formData.email, formData.password, formData.company_name, formData.phone, captchaToken, isCompany, fiscal);
      toast.success(res.message || t('Cuenta creada. Revisa tu email para verificar.'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error al crear cuenta'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    t('Respuesta en menos de 1 hora'),
    t('Soporte técnico 24/7'),
    t('Garantía en todos los archivos'),
    t('Precios competitivos'),
  ];

  const inputClass = "bg-surface-highlight border-white/10 text-white placeholder:text-zinc-600 h-11";

  return (
    <div className="min-h-screen bg-background flex">
      <div className="noise-overlay" />
      
      {/* Left Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/279949/pexels-photo-279949.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50" />
        <div className="absolute inset-0 flex flex-col justify-center p-12">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo-ar.png" alt="AR Performance" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                AR PERFORMANCE
              </h1>
              <p className="text-sm text-zinc-400 uppercase tracking-wider">FileService</p>
            </div>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white mb-4">
            {t('ÚNETE A LA')}<br />
            <span className="text-primary">{t('COMUNIDAD')}</span>
          </h2>
          <p className="text-zinc-400 max-w-md mb-8">
            {t('Más de 500 profesionales confían en nosotros para sus archivos de chiptuning.')}
          </p>
          
          <div className="space-y-3">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-zinc-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto relative">
        <div className="absolute top-4 right-4"><LanguageSelector /></div>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src="/logo-ar.png" alt="AR Performance" className="w-10 h-10 object-contain" />
            <span className="font-heading text-xl font-bold text-white">AR PERFORMANCE</span>
          </div>

          <div className="text-center mb-6">
            <h1 className="font-heading text-3xl font-bold text-white mb-2">{t('CREAR CUENTA')}</h1>
            <p className="text-zinc-400">{t('Regístrate para empezar a trabajar')}</p>
          </div>

          {/* Company toggle */}
          <div className="flex gap-2 mb-5">
            <button
              type="button"
              onClick={() => setIsCompany(false)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md border text-sm font-medium transition-all ${
                !isCompany ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              <User className="w-4 h-4" />{t('Particular')}
            </button>
            <button
              type="button"
              onClick={() => setIsCompany(true)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md border text-sm font-medium transition-all ${
                isCompany ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              <Building2 className="w-4 h-4" />{t('Empresa')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="company_name" className="text-zinc-300">{isCompany ? t('Nombre de Empresa') : t('Nombre Completo / Taller')}</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder={isCompany ? "Mi Empresa S.L." : "Juan García / Mi Taller"}
                required
                className={inputClass}
                data-testid="register-company-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="tu@email.com"
                required
                className={inputClass}
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-zinc-300">{t('Teléfono (Opcional)')}</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+34 600 000 000"
                className={inputClass}
                data-testid="register-phone-input"
              />
            </div>

            {/* Fiscal Data Section */}
            <div className="border border-white/10 rounded-md p-4 space-y-3 bg-surface-highlight/30">
              <p className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                {t('Datos Fiscales')} {!isCompany && <span className="text-zinc-500">({t('Opcional')})</span>}
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-zinc-400 text-xs">{isCompany ? t('Razón Social') : t('Nombre Completo')}</Label>
                  <Input name="fiscal_name" value={fiscalData.fiscal_name} onChange={handleFiscalChange}
                    placeholder={isCompany ? "Mi Empresa S.L." : "Juan García López"}
                    className={inputClass} />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">{isCompany ? 'CIF' : 'DNI/NIE'}</Label>
                  <Input name="cif" value={fiscalData.cif} onChange={handleFiscalChange}
                    placeholder={isCompany ? "B12345678" : "12345678A"}
                    className={inputClass} />
                </div>
                {isCompany && (
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Nº IVA (Intracom.)</Label>
                    <Input name="vat_number" value={fiscalData.vat_number} onChange={handleFiscalChange}
                      placeholder="ESB12345678" className={inputClass} />
                  </div>
                )}
                <div className={`space-y-1 ${isCompany ? 'col-span-2' : ''}`}>
                  <Label className="text-zinc-400 text-xs">{t('Dirección')}</Label>
                  <Input name="fiscal_address" value={fiscalData.fiscal_address} onChange={handleFiscalChange}
                    placeholder="Calle Principal, 1" className={inputClass} />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">{t('Ciudad')}</Label>
                  <Input name="fiscal_city" value={fiscalData.fiscal_city} onChange={handleFiscalChange}
                    placeholder="Cáceres" className={inputClass} />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">{t('Código Postal')}</Label>
                  <Input name="fiscal_postal_code" value={fiscalData.fiscal_postal_code} onChange={handleFiscalChange}
                    placeholder="10001" className={inputClass} />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">{t('Provincia')}</Label>
                  <Input name="fiscal_province" value={fiscalData.fiscal_province} onChange={handleFiscalChange}
                    placeholder="Cáceres" className={inputClass} />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">{t('País')}</Label>
                  <Input name="fiscal_country" value={fiscalData.fiscal_country} onChange={handleFiscalChange}
                    placeholder="España" className={inputClass} />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-zinc-300">{t('Contraseña')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className={`${inputClass} pr-12`}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-zinc-300">{t('Confirmar Contraseña')}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className={inputClass}
                data-testid="register-confirm-password-input"
              />
            </div>

            <CaptchaWidget onVerify={setCaptchaToken} />

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-12"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('Creando cuenta...')}
                </>
              ) : (
                t('Crear Cuenta')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              {t('¿Ya tienes cuenta?')}{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                {t('Inicia sesión')}
              </Link>
            </p>
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="text-zinc-500 hover:text-white text-sm">
              ← {t('Volver al inicio')}
            </Link>
          </div>
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default RegisterPage;
